import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { useRouter } from "next/router"

import { RUNS_FILTER_STORAGE_KEY } from "@/constants/storage-keys"
import { useScopedStorageKey } from "@/workspaces/scope"
import { toast } from "@coval/ui"

import { useAnalytics } from "@/lib/analytics"
import { useFilterParams } from "@/hooks/filter-params/useFilterParams"

import { useRunsFilterActions } from "../hooks/useRunsFilterActions"
import { useRunsViewState } from "../hooks/useRunsViewState"
import type {
  FilterState,
  RunsFilterContextType,
  RunsSortDescriptor,
} from "../types/filterState"
import type { RunsSavedViewsClient } from "../types/savedViews"
import {
  DEFAULT_RUNS_FILTER_STATE,
  RUNS_FILTER_PARAM_SCHEMA,
} from "../utils/filterStateConfig"

const RunsFilterContext = createContext<RunsFilterContextType | undefined>(
  undefined,
)

// ── Provider ──────────────────────────────────────────────────

interface RunsFilterProviderProps {
  children: ReactNode
  /** Skip localStorage read/write — start fresh and don't persist */
  disablePersistence?: boolean
  savedViewsClient?: RunsSavedViewsClient
}

const emptySavedViewsClient: RunsSavedViewsClient = {
  fetchUserDefaultView: () => Promise.resolve(null),
  fetchView: () => Promise.resolve(null),
}

export const RunsFilterProvider: React.FC<RunsFilterProviderProps> = ({
  children,
  disablePersistence = false,
  savedViewsClient = emptySavedViewsClient,
}) => {
  const router = useRouter()
  const { trackEvent } = useAnalytics()

  // Scoped by org + workspace so switching either can never resurrect a
  // filter from the previous scope.
  const scopedStorageKey = useScopedStorageKey(RUNS_FILTER_STORAGE_KEY)

  const {
    state,
    setField: rawSetField,
    updateField: rawUpdateField,
    setFields: rawSetFields,
    resetAll,
    isReady,
  } = useFilterParams<FilterState>({
    schema: RUNS_FILTER_PARAM_SCHEMA,
    defaults: DEFAULT_RUNS_FILTER_STATE,
    storageKey: disablePersistence ? undefined : scopedStorageKey,
  })

  const [searchResetKey, setSearchResetKey] = useState(0)
  const debouncedSearchValue = state.searchValue
  const showToast = useCallback(
    ({
      title,
      description,
      color,
    }: {
      title: string
      description?: string
      color?: "danger"
    }) => {
      if (color === "danger") {
        toast.danger({ title, description })
        return
      }

      toast.success({ title, description })
    },
    [],
  )

  const viewState = useRunsViewState({
    isReady,
    router,
    state,
    rawSetFields,
    savedViewsClient,
    showToast,
  })

  const setField = useCallback(
    <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
      viewState.clearLoadedView()
      if (key === "searchValue" && value === "") {
        setSearchResetKey((current) => current + 1)
      }
      rawSetField(key, value)
    },
    [rawSetField, viewState],
  )

  const updateField = useCallback(
    <K extends keyof FilterState>(
      key: K,
      updater: (prev: FilterState[K]) => FilterState[K],
    ) => {
      viewState.clearLoadedView()
      rawUpdateField(key, updater)
    },
    [rawUpdateField, viewState],
  )

  const resetFilters = useCallback(
    (sortDescriptor: RunsSortDescriptor) => {
      setSearchResetKey((current) => current + 1)
      viewState.clearLoadedView({ updateUrl: false })
      resetAll({
        overrides: { sortDescriptor },
        removeUrlKeys: ["agent_id", "viewId"],
      })
    },
    [resetAll, viewState],
  )

  const clearUrlAgentFilter = useCallback(() => {
    viewState.clearLoadedView({ updateUrl: false })
    resetAll({
      overrides: { ...state, agentFilters: [] },
      removeUrlKeys: ["agent_id", "viewId"],
    })
  }, [resetAll, state, viewState])

  const filterActions = useRunsFilterActions({
    state,
    setField,
    updateField,
    resetFilters,
    trackEvent,
  })

  // ── Context value ──────────────────────────────────────────

  const value: RunsFilterContextType = useMemo(
    () => ({
      searchValue: state.searchValue,
      searchResetKey,
      debouncedSearchValue,
      dateRange: state.dateRange,
      statusFilters: state.statusFilters,
      templateFilters: state.templateFilters,
      personaFilters: state.personaFilters,
      agentFilters: state.agentFilters,
      clearUrlAgentFilter,
      metricFilters: state.metricFilters,
      metricValueFilters: state.metricValueFilters,
      tagFilters: state.tagFilters,
      mutationFilters: state.mutationFilters,
      testSetFilters: state.testSetFilters,
      testCaseFilters: state.testCaseFilters,
      metadataFilters: state.metadataFilters,
      scheduledJobFilters: state.scheduledJobFilters,
      sortDescriptor: state.sortDescriptor,
      isReady,
      ...filterActions,
      loadedViewId: viewState.loadedViewId,
      applyView: (view) => {
        setSearchResetKey((current) => current + 1)
        viewState.applyView(view)
      },
      clearLoadedView: viewState.clearLoadedView,
    }),
    [
      debouncedSearchValue,
      clearUrlAgentFilter,
      filterActions,
      isReady,
      searchResetKey,
      state,
      viewState,
    ],
  )

  return (
    <RunsFilterContext.Provider value={value}>
      {children}
    </RunsFilterContext.Provider>
  )
}

export const useRunsFilter = () => {
  const context = useContext(RunsFilterContext)
  if (context === undefined) {
    throw new Error("useRunsFilter must be used within a RunsFilterProvider")
  }
  return context
}
